import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store';
import { fetchAllGamicsAdmin } from '@/store/Gamic';
import FilterSelect from '@/components/filters/FilterSelect';
import Search from '@/components/filters/Search';
import GamicsTable from './components/GamicsTable';
import { AdminGamic, Status } from './types';
import AnalyticsCard from '@/components/cards/AnalyticsCard';
import { fetchCategories } from '@/store/Category';

const mockStatus: (Status | 'All')[] = [
  'All',
  'Pending',
  'Rejected',
  'Approved',
  'Hold',
];

const AdminGamics: React.FC = () => {
  const [category, setCategory] = useState('All');
  const [categories, setCategories] = useState<string[]>(['All']);
  const [status, setStatus] = useState<Status | 'All'>('All');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const dispatch = useDispatch<AppDispatch>();
  const dispatchForC = useDispatch<any>();
  const {
    adminList = [],
    adminPagination,
    adminStats,
    status: sliceStatus,
  } = useSelector((s: RootState) => s.gamics);

  const totalPages = adminPagination?.totalPages ?? 1;
  const rows = useMemo(
    () => (Array.isArray(adminList) ? adminList : []),
    [adminList],
  );

  const stats = useMemo(
    () => ({
      total: Number(adminStats?.total ?? 0),
      approved: Number(adminStats?.approved ?? 0),
      pending: Number(adminStats?.pending ?? 0),
      rejected: Number(adminStats?.rejected ?? 0),
    }),
    [adminStats],
  );

  const pct = (n: number) =>
    stats.total ? Math.round((n / stats.total) * 100) : 0;

  const cards = [
    {
      key: 'total',
      label: 'Total Gamics',
      value: stats.total,
      percentage: 100,
    },
    {
      key: 'approved',
      label: 'Approved',
      value: stats.approved,
      percentage: pct(stats.approved),
    },
    {
      key: 'pending',
      label: 'Pending',
      value: stats.pending,
      percentage: pct(stats.pending),
    },
    {
      key: 'rejected',
      label: 'Rejected',
      value: stats.rejected,
      percentage: pct(stats.rejected),
    },
  ];

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await dispatchForC(fetchCategories());
        const data = res.payload?.data || [];
        const names = ['All', ...data.map((item: any) => item.name)];
        setCategories(names);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    loadCategories();
  }, []);

  /* ─── first load & filters change ───────────────────────────────────────── */
  useEffect(() => {
    setPage(1); // Reset to page 1 on filter change
    dispatch(
      fetchAllGamicsAdmin({
        title: search || undefined,
        category: category !== 'All' ? category : undefined,
        status: status !== 'All' ? status.toLowerCase() : undefined,
        page: 1,
        limit: 20,
      }),
    );
  }, [dispatch, search, category, status]);

  /* ─── infinite-scroll callback ──────────────────────────────────────────── */
  const loadMore = () => {
    if (page >= totalPages || sliceStatus === 'loading') return;
    const next = page + 1;
    setPage(next);
    dispatch(
      fetchAllGamicsAdmin({
        title: search || undefined,
        category: category !== 'All' ? category : undefined,
        status: status !== 'All' ? status.toLowerCase() : undefined,
        page: next,
        limit: 20,
      }),
    );
  };

  /* ─── map API → table model ─────────────────────────────────────────────── */
  const formatData = (dataArr: any[]): AdminGamic[] =>
    dataArr.map((g) => ({
      id: g.id,
      file: g.file_url,
      img: g.image_url,
      timestamp: g.created_at,
      title: g.title,
      category: g.categories?.[0] || 'Uncategorized',
      tags: g.tags,
      description: g.description || 'No description provided',
      byUser: g.user_id,
      downloadLink: g.download_url,
      isDownloadLink: g.download_url ? 'Attached' : 'Not Attached',
      consoleResult: 'Pass',
      status: (g.status?.charAt(0).toUpperCase() +
        g.status?.slice(1)) as Status,
    }));

  /* ─── render ────────────────────────────────────────────────────────────── */
  return (
    <div className='flex h-full flex-col'>
      <div className='grid mb-4 grid-cols-4 gap-4'>
        {cards.map((c) => (
          <AnalyticsCard key={c.key} label={c.label} value={c.value} />
        ))}
      </div>
      <div className='p-8 rounded-[24px] bg-white flex flex-col h-full min-h-0 space-y-[24px]'>
        {/* ─── header & filters ───────────────────────────────────────────── */}
        <div className='flex items-center gap-2 justify-between'>
          <div className='text-[26px] font-[600]'>
            Showing {rows.length} Results
          </div>

          <div className='flex-wrap inline-flex items-center gap-2 lg:gap-0 space-x-[10px]'>
            <Search
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <FilterSelect
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              options={[
                { value: 'All', label: 'Category: All' },
                ...categories
                  .filter((c) => c !== 'All')
                  .map((c) => ({ value: c, label: `Category: ${c}` })),
              ]}
            />
            <FilterSelect
              value={status}
              onChange={(e) => setStatus(e.target.value as Status | 'All')}
              options={mockStatus.map((s) => ({
                value: s,
                label: `Status: ${s}`,
              }))}
            />
          </div>
        </div>

        {/* ─── table ──────────────────────────────────────────────────────── */}
        <div className='flex-1 min-h-0'>
          <GamicsTable
            data={formatData(rows)}
            loadMore={loadMore}
            hasMore={page < totalPages}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminGamics;
